import {
  EmoBriefcase,
  EmoCart,
  EmoEye,
  EmoGift,
  EmoMega,
  EmoMic,
  EmoNeutral,
  EmoPin,
  EmoWorld,
} from "@assets"
import { Head, Link, router } from "@inertiajs/react"
import { useEffect, useMemo, useState } from "react"

import { FeedHero } from "@/components/feed/feed-hero"
import FeedItem, {
  type FeedItem as FeedItemComponent,
} from "@/components/feed/feed-item"
import { PersonalizedCards } from "@/components/feed/personalized-cards"
import type { BackendStory } from "@/components/stories"
import { Stories } from "@/components/stories"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/hooks/use-auth"
import { useDragScroll } from "@/hooks/use-drag-scroll"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import { feedPath } from "@/routes"

interface FeedItemData {
  id: string
  type:
    | "event"
    | "forum_post"
    | "raffle"
    | "product"
    | "business"
    | "interview"
    | "job"
    | "announcement"
    | "story"
  created_at?: string
  title?: string
  content?: string
  description?: string
  image_url?: string
  media_url?: string
  author?: {
    id: number
    name: string
    avatar_url?: string
  }
  views_count?: number
  viewed?: boolean
  is_own_story?: boolean
  url_path?: string
  [key: string]: unknown
}

interface PersonalizedData {
  stats: {
    events_this_weekend: number
    active_raffles: number
    unanswered_questions: number
  }
  daysSinceLastVisit?: number
  missed?: {
    new_events_in_neighborhood: number
    answers_on_your_question: number
    raffles_ended: number
  }
  neighborhood_activity: {
    type: "event_rsvp" | "new_business" | "forum_activity"
    text: string
    timestamp: string
    url?: string
  }[]
  weeklyChallenges?: {
    challenges: {
      key: string
      title: string
      points: number
      icon: string
      completed: boolean
    }[]
    days_remaining: number
    all_completed: boolean
    total_possible_points: number
  }
  hero_stats?: {
    total_users: number
    total_businesses: number
  }
}

interface FeedProps {
  feed_items: FeedItemData[]
  currentFilter?: string
  filters: {
    city: string
    neighborhood?: string
  }
  pagination: {
    current_offset: number
    limit: number
    total: number
    has_more: boolean
  }
  personalized?: PersonalizedData
}

export default function FeedIndex({
  feed_items = [],
  currentFilter = "todos",
  pagination,
  personalized,
}: FeedProps) {
  const { user, isAuthenticated } = useAuth()
  const { ref: filtersRef, onMouseDown: onFiltersMouseDown } = useDragScroll()
  const [allFeedItems, setAllFeedItems] = useState(feed_items)
  const [loadingMore, setLoadingMore] = useState(false)

  // Scroll to filters section when navigating from challenge
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get("scrollToFilters") === "true") {
      setTimeout(() => {
        const filtersSection = document.getElementById("feed-filters")
        if (filtersSection) {
          filtersSection.scrollIntoView({ behavior: "smooth", block: "start" })
        }
      }, 100)
    }
  }, [currentFilter])

  // Separar historias del resto del feed (memoized)
  const stories = useMemo(
    () =>
      allFeedItems.filter(
        (item): item is FeedItemData & BackendStory => item.type === "story",
      ),
    [allFeedItems],
  )
  const feedContent = useMemo(
    () => allFeedItems.filter((item) => item.type !== "story"),
    [allFeedItems],
  )

  useEffect(() => {
    if (feed_items.length > 0) {
      setAllFeedItems((prevItems) => {
        if (pagination.current_offset === 0) {
          return feed_items
        } else {
          const existingIds = new Set(prevItems.map((item) => item.id))
          const newItems = feed_items.filter(
            (newItem) => !existingIds.has(newItem.id),
          )
          return newItems.length > 0 ? [...prevItems, ...newItems] : prevItems
        }
      })
    } else if (feed_items.length === 0 && pagination.current_offset === 0) {
      setAllFeedItems([])
    }
  }, [feed_items, pagination.current_offset])

  const getFilterHref = (filterKey: string) => {
    return filterKey === "todos"
      ? feedPath()
      : `${feedPath()}?filter=${filterKey}`
  }

  const handleLoadMore = () => {
    if (loadingMore || !pagination.has_more) return

    setLoadingMore(true)
    const nextOffset = pagination.current_offset + pagination.limit

    router.get(
      feedPath(),
      {
        filter: currentFilter === "todos" ? undefined : currentFilter,
        offset: nextOffset,
      },
      {
        preserveState: true,
        preserveScroll: true,
        only: ["feed_items", "pagination"],
        onFinish: () => setLoadingMore(false),
      },
    )
  }

  return (
    <AppLayout>
      <Head title="Feed" />
      <div className="bg-background">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <Stories backendStories={stories as BackendStory[]} />

          {/* Hero for unauthenticated visitors */}
          {!isAuthenticated && personalized && (
            <FeedHero
              stats={personalized.stats}
              heroStats={personalized.hero_stats}
            />
          )}

          {/* Personalized Section - "Para Vos" + "Qué Está Pasando" */}
          {isAuthenticated && personalized && user?.neighborhood && (
            <PersonalizedCards
              stats={personalized.stats}
              userName={user?.name || "Usuario"}
              activities={personalized.neighborhood_activity}
              neighborhoodName={user?.neighborhood.name}
              daysSinceLastVisit={personalized.daysSinceLastVisit}
              missed={personalized.missed}
              weeklyChallenges={personalized.weeklyChallenges}
              userPoints={user?.points}
            />
          )}

          <div className="px-[15px] pb-[15px] md:px-0">
            {/* Filters */}
            <div
              id="feed-filters"
              ref={filtersRef}
              onMouseDown={onFiltersMouseDown}
              className="scrollbar-hide-x mt-[18px] w-full cursor-grab overflow-x-auto select-none active:cursor-grabbing"
            >
              <div className="flex flex-nowrap justify-start gap-[7px]">
                {[
                  { key: "todos", label: "Todo", icon: EmoWorld },
                  { key: "eventos", label: "Eventos", icon: EmoPin },
                  { key: "sorteos", label: "Sorteos", icon: EmoGift },
                  { key: "foro", label: "Foro", icon: EmoEye },
                  { key: "entrevistas", label: "Entrevistas", icon: EmoMic },
                  { key: "curros", label: "Curros", icon: EmoBriefcase },
                  { key: "mercado", label: "Mercado", icon: EmoCart },
                  { key: "anuncios", label: "Anuncios", icon: EmoMega },
                ].map((filter) => (
                  <Link
                    key={filter.key}
                    href={getFilterHref(filter.key)}
                    prefetch="hover"
                    preserveScroll
                    className={cn(
                      "focus-visible:ring-ring inline-flex items-center justify-center text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
                      "h-auto min-w-0 flex-shrink-0 gap-[4px] rounded-[14px] px-[7px] py-[8px]",
                      currentFilter === filter.key
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground bg-card font-semibold",
                    )}
                  >
                    <span className="bg-card flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-full">
                      <img src={filter.icon} alt="" />
                    </span>
                    <span className="xs:inline hidden sm:inline">
                      {filter.label}
                    </span>
                    <span className="xs:hidden text-[14px] tracking-[0.28px] sm:hidden">
                      {filter.label}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-background w-full">
            {feedContent.length > 0 ? (
              <>
                {feedContent.map((item, index) => (
                  <FeedItem
                    key={`${item.type}-${item.id}`}
                    item={item as unknown as FeedItemComponent}
                    isLast={index === feedContent.length - 1}
                  />
                ))}

                {pagination.has_more && (
                  <div className="p-4 text-center">
                    <Button
                      variant="ghost"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="text-primary hover:bg-muted"
                    >
                      {loadingMore ? "Cargando..." : "Ver más publicaciones"}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div
                className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
                style={{ background: "var(--gradient-highlight)" }}
              >
                <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
                  <img src={EmoNeutral} alt="Sin contenido" />
                </div>

                <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
                  No se encontró
                  <br />
                  contenido disponible
                </h3>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
